// 과제/업무 관리 표에서 볼 유형(사업~수행업무)과 컬럼을 고르는 드롭다운

import { useMemo } from 'react'
import CheckDropdown, { usePersistedVisible, type CheckOption } from './CheckDropdown'
import type { TaskTreeNode } from './TaskHierarchyTable'

export type TaskRowType = TaskTreeNode['type']

const TASK_ROW_TYPES: CheckOption<TaskRowType>[] = [
  { value: 'business', label: '사업' },
  { value: 'task_group', label: '전략과제' },
  { value: 'task_subgroup', label: '실행과제' },
  { value: 'task', label: '세부실행과제' },
  { value: 'task_item', label: '수행업무' },
]
const ALL_TYPES = TASK_ROW_TYPES.map(option => option.value)

/** 숨길 수 있는 컬럼 (field 기준). 명칭은 항상 보인다. */
const TASK_COLUMNS: CheckOption<string>[] = [
  { value: 'action', label: '액션' },
  { value: 'type', label: '유형' },
  { value: 'task_item_status', label: '수행업무상태' },
  { value: 'task_status', label: '세부실행과제상태' },
  { value: 'task_performance_status', label: '성과상태' },
  { value: 'department_name', label: '부서' },
  { value: 'assignee', label: '담당자' },
  { value: 'manager', label: '관리자' },
  { value: 'reviewer', label: '검토자' },
  { value: 'labels', label: '라벨' },
]
const ALL_COLUMNS = TASK_COLUMNS.map(option => option.value)

/** 표에 보일 유형. 모두 고르면 필터가 없는 것과 같다. */
export const usePersistedTaskTypes = () => {
  const { visible, setVisible } = usePersistedVisible('dot_task_hidden_types', ALL_TYPES)
  return { visibleTypes: visible, setVisibleTypes: setVisible }
}

/** 표에서 숨긴 컬럼 field 목록 */
export const usePersistedTaskColumns = () => {
  const { visible, setVisible } = usePersistedVisible('dot_task_hidden_columns', ALL_COLUMNS)
  const hiddenColumns = useMemo(() => ALL_COLUMNS.filter(field => !visible.includes(field)), [visible])
  return { visibleColumns: visible, setVisibleColumns: setVisible, hiddenColumns }
}

interface TaskTypeFilterProps {
  visibleTypes: TaskRowType[]
  onChange: (types: TaskRowType[]) => void
}

export default function TaskTypeFilter({ visibleTypes, onChange }: TaskTypeFilterProps) {
  return (
    <CheckDropdown
      options={TASK_ROW_TYPES}
      visible={visibleTypes}
      onChange={onChange}
      allLabel="[전체] 유형 선택"
      partialLabel={count => `유형 ${count}개 선택`}
      minSelected={1}
    />
  )
}

interface TaskColumnFilterProps {
  visibleColumns: string[]
  onChange: (columns: string[]) => void
}

export function TaskColumnFilter({ visibleColumns, onChange }: TaskColumnFilterProps) {
  return (
    <CheckDropdown
      options={TASK_COLUMNS}
      visible={visibleColumns}
      onChange={onChange}
      allLabel="[전체] 컬럼 선택"
      partialLabel={count => (count === 0 ? '명칭만 표시' : `컬럼 ${count}개 표시`)}
    />
  )
}
